import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsLeft, ChevronsRight, LogOut, Volume2, VolumeX } from "lucide-react";

import { useListIncidents, useLogout } from "@/api/generated";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isInternalRole, useMe } from "@/lib/lookups";
import { cn } from "@/lib/utils";
import { useUI } from "@/store/ui";
import { NAV } from "./nav";

export function Logo({ expanded }: { expanded: boolean }) {
  return (
    <div className="flex h-12 items-center gap-2 px-3">
      <svg viewBox="0 0 24 24" className="size-6 shrink-0 text-primary" aria-hidden>
        <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <ellipse cx="12" cy="12" rx="4.5" ry="9.5" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
        <path d="M2.5 12h19M4.5 7h15M4.5 17h15" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      </svg>
      {expanded && <span className="text-sm font-semibold tracking-wide">Atlas</span>}
    </div>
  );
}

export function NavList({ expanded, onNavigate }: { expanded: boolean; onNavigate?: () => void }) {
  const role = useMe().data?.role;
  const internal = isInternalRole(role);
  const incidents = useListIncidents(undefined, { query: { enabled: internal } });
  const openCount = (incidents.data ?? []).filter((i) => i.status === "OPEN" && !i.suppressed_by).length;
  return (
    <nav className="flex flex-col gap-0.5 px-2">
      {NAV.filter((n) => role && n.roles.includes(role)).map((n) => {
        const Icon = n.icon;
        const badge = n.to === "/incidents" && openCount > 0 ? openCount : null;
        const link = (
          <Link
            to={n.to}
            onClick={onNavigate}
            aria-label={expanded ? undefined : n.label}
            activeOptions={{ exact: !!n.exact }}
            className={cn(
              "relative flex h-9 items-center gap-3 rounded-md px-2 text-sm text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              "data-[status=active]:bg-sidebar-accent data-[status=active]:text-sidebar-accent-foreground data-[status=active]:font-medium",
            )}
          >
            <Icon className="size-4 shrink-0" />
            {expanded && <span className="truncate">{n.label}</span>}
            {badge !== null && (
              <span
                className={cn(
                  "tabular rounded-full bg-status-down px-1.5 text-[10px] leading-4 font-semibold text-white",
                  expanded ? "ml-auto" : "absolute top-0.5 right-0.5",
                )}
              >
                {badge}
              </span>
            )}
          </Link>
        );
        return expanded ? (
          <div key={n.to + n.label}>{link}</div>
        ) : (
          <Tooltip key={n.to + n.label}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{n.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}

export function UserMenu({ expanded }: { expanded: boolean }) {
  const me = useMe().data;
  const logout = useLogout();
  const qc = useQueryClient();
  const navigate = useNavigate();
  if (!me) return null;
  const initials = me.display_name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex h-10 w-full items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-sidebar-accent">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-semibold text-primary">{initials}</span>
          {expanded && (
            <span className="min-w-0">
              <span className="block truncate text-xs font-medium">{me.display_name}</span>
              <span className="block text-[10px] text-muted-foreground">{me.role}</span>
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm">{me.display_name}</div>
          <div className="text-xs font-normal text-muted-foreground">{me.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            logout.mutate(undefined, {
              onSettled: () => {
                qc.clear();
                navigate({ to: "/login" });
              },
            })
          }
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SoundToggle({ expanded }: { expanded: boolean }) {
  const audible = useUI((s) => s.audible);
  const setAudible = useUI((s) => s.setAudible);
  const Icon = audible ? Volume2 : VolumeX;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size={expanded ? "sm" : "icon-sm"}
          className={cn(expanded && "w-full justify-start", audible && "text-primary")}
          onClick={() => setAudible(!audible)}
          aria-pressed={audible}
        >
          <Icon /> {expanded && (audible ? "Sound on" : "Sound off")}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">Audible alert for new DOWN or CRITICAL incidents</TooltipContent>
    </Tooltip>
  );
}

export function Rail() {
  const expanded = useUI((s) => s.railExpanded);
  const toggle = useUI((s) => s.toggleRail);
  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col border-r bg-sidebar transition-[width] duration-150 md:flex",
        expanded ? "w-60" : "w-12",
      )}
    >
      <Logo expanded={expanded} />
      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        <NavList expanded={expanded} />
      </div>
      <div className={cn("flex flex-col gap-1 border-t p-2", !expanded && "items-center")}>
        <SoundToggle expanded={expanded} />
        <UserMenu expanded={expanded} />
        <Button variant="ghost" size={expanded ? "sm" : "icon-sm"} className={cn(expanded && "w-full justify-start")} onClick={toggle} aria-label={expanded ? "Collapse navigation" : "Expand navigation"}>
          {expanded ? <ChevronsLeft /> : <ChevronsRight />} {expanded && "Collapse"}
        </Button>
      </div>
    </aside>
  );
}
